/*
  The two-up / one-up control at the end of the collection page's filter row.

  All this file does is record which of the two buttons is pressed. It sets no width, no
  column count and no size: the choice becomes a class on the section element and
  assets/kl-collection.css is what turns that class into tracks. Nothing here knows how wide
  a product card is, which is the point — the grid can be re-laid out entirely in the
  stylesheet and this file goes on working.

  The class goes on the section rather than on the grid, and that is the one decision worth
  explaining. assets/facets.js replaces the whole of #ProductGridContainer every time a
  filter or a sort changes, so a class written onto the <ul> inside it would be discarded
  the first time a shopper touched a filter — and silently, leaving the buttons saying one
  thing and the grid showing another. The section element is outside everything Dawn
  replaces, so the choice survives, and the freshly fetched grid inherits it on arrival with
  nothing to re-apply.

  Nothing is persisted between page loads on purpose. The control is a way of looking at one
  list rather than a preference, and a shopper who has left the page and come back gets the
  layout the merchant chose.

  WHY THE WHOLE FILE IS INSIDE A FUNCTION, because it looks like ceremony and is not. A
  <script src> without type="module" shares one scope with every other such script on the
  page, so a top-level `const` here is a global in the plainest sense. This file and
  assets/kl-collection-sort-sync.js both wanted to name the section's hook, both called it
  COLLECTION_SELECTOR, and the second one the browser parsed threw "Identifier already been
  declared" — a parse error, so not one line of it ran. The control drew perfectly and did
  nothing at all when clicked.

  CLAUDE.md's "no globals" rule is what this violates, and the rule turns out to mean this
  literally: not only "do not hang state on window" but "do not put a name in the shared
  scope". A file that keeps its names to itself cannot collide with one written next year by
  someone who never opened it.
*/

(() => {
  const GRID_TOGGLE_SELECTOR = '[data-kl-grid-toggle]';
  const GRID_TOGGLE_OPTION_SELECTOR = '[data-kl-grid-toggle-columns]';
  const COLLECTION_SELECTOR = '[data-kl-collection]';
  const PRODUCT_GRID_SELECTOR = '#product-grid';

  const ACTIVE_CLASS = 'is-active';
  const COLUMNS_CLASS_PREFIX = 'kl-collection--mobile-columns-';
  const COLUMNS_VALUE_BY_COUNT = {
    1: 'minmax(0, 1fr)',
    2: 'repeat(2, minmax(0, 1fr))',
  };

  /*
    Pure and DOM-free: which class the section should carry for a given choice. One place, so
    the class the markup is given and the class the stylesheet reads cannot drift apart.
  */
  function getColumnsClass(columns) {
    return `${COLUMNS_CLASS_PREFIX}${columns}`;
  }

  function updateColumns(collectionElement, optionElements, columns) {
    const productGridElement = collectionElement.querySelector(PRODUCT_GRID_SELECTOR);
    const columnsValue = COLUMNS_VALUE_BY_COUNT[columns];

    if (!productGridElement || !columnsValue) return;

    optionElements.forEach((optionElement) => {
      const isChosen = optionElement.dataset.klGridToggleColumns === columns;

      optionElement.classList.toggle(ACTIVE_CLASS, isChosen);
      optionElement.setAttribute('aria-pressed', isChosen ? 'true' : 'false');

      /*
        Every option's class comes off before the chosen one goes on, so a third choice added
        to the markup later needs no change here.
      */
      collectionElement.classList.remove(getColumnsClass(optionElement.dataset.klGridToggleColumns));
    });

    collectionElement.classList.add(getColumnsClass(columns));
    productGridElement.style.setProperty('--collection-columns-mobile', columns);
  }

  function syncGridToggle(toggleElement) {
    const collectionElement = toggleElement.closest(COLLECTION_SELECTOR);
    const optionElements = Array.from(toggleElement.querySelectorAll(GRID_TOGGLE_OPTION_SELECTOR));
    if (!collectionElement || optionElements.length === 0) return;
    const activeOptionElement = optionElements.find((optionElement) => optionElement.classList.contains(ACTIVE_CLASS));
    if (activeOptionElement) {
      updateColumns(collectionElement, optionElements, activeOptionElement.dataset.klGridToggleColumns);
    }
  }

  document.addEventListener('click', (event) => {
    const optionElement = event.target.closest(GRID_TOGGLE_OPTION_SELECTOR);
    if (!optionElement) return;

    const toggleElement = optionElement.closest(GRID_TOGGLE_SELECTOR);
    const collectionElement = optionElement.closest(COLLECTION_SELECTOR);
    if (!toggleElement || !collectionElement) return;

    updateColumns(
      collectionElement,
      Array.from(toggleElement.querySelectorAll(GRID_TOGGLE_OPTION_SELECTOR)),
      optionElement.dataset.klGridToggleColumns
    );
  });

  document.querySelectorAll(GRID_TOGGLE_SELECTOR).forEach(syncGridToggle);
})();
